import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FaShareAlt } from 'react-icons/fa';
import logger from '../utils/logger';
import BookmarkForm from './BookmarkForm';
import AuthForm from './AuthForm';
// VoiceControl moved to HamburgerMenu
import ImportDialog from './ImportDialog';
import DataSyncDialog from './DataSyncDialog';
import MobileHeader from './MobileHeader';
import TabNavigation from './TabNavigation';
import CompactBookmarkItem from './CompactBookmarkItem';
import Settings from './Settings';
import Support from './Support';
import VersionInfo from './VersionInfo';
import { APP_VERSION } from '../version';
import CategoryManagement from './CategoryManagement';
import ExportDialog from './ExportDialog';
import Archive from './Archive';
import ThemeModal from './ThemeModal';
import PrivacyPolicy from './PrivacyPolicy';
import {
  RemindersPlaceholder,
  UpgradeModal
} from './PremiumPlaceholders';
import RecipesList from './recipes/RecipesList';
import QuickShoppingList from './recipes/QuickShoppingList';
import QuickTodoList from './recipes/QuickTodoList';
import JoinShareHandler from './recipes/JoinShareHandler';
import OfflineBanner from './OfflineBanner';
import OnboardingFlow from './Onboarding/OnboardingFlow';
import SyncStatusIndicator from './SyncStatusIndicator';
import ConflictResolutionModal from './ConflictResolutionModal';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useCategories } from '../contexts/CategoryContext';
import APP_CONFIG from '../config/appConfig';
import bookmarkApi from '../services/bookmarkApi';
import importApi from '../services/importApi';
import errorTrackingService from '../services/errorTrackingService';
import offlineQueueService, { EntityType, OperationType } from '../services/offlineQueueService';
import { offlineDb, STORES } from '../services/offlineDatabase';
import { setupAutoBackup } from '../utils/dataBackup';
import { archiveItem, ENTITY_TYPES, ARCHIVE_REASONS } from '../utils/archiveManager';
import { stripCategoryPrefix } from '../utils/categoryKeywords';
import '../styles/App.css';
import '../styles/DarkTheme.css';
import '../styles/CategoryManagement.css';
import '../styles/DynamicPanel.css';
import '../styles/CustomThemes.css';
import RemindersTab from '../reminders/RemindersTab';

const App = () => {
  const { language, switchLanguage, t } = useLanguage();
  const { user, loading: authLoading, isAuthenticated, completeOnboarding } = useAuth();
  const { categories } = useCategories();
  
  // App State
  const [bookmarks, setBookmarks] = useState([]);
  const [editingBookmark, setEditingBookmark] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [apiAvailable, setApiAvailable] = useState(true);
  const [forceOnlineMode, setForceOnlineMode] = useState(false);

  // 🧭 ONBOARDING: einmaliger Wizard direkt nach der Registrierung.
  // Gesteuert über user.onboardingCompleted vom Server (siehe AuthContext.js).
  const [showOnboarding, setShowOnboarding] = useState(false);

  // Dynamic Content Panel States
  const [activePanel, setActivePanel] = useState(null); // 'categories', 'export', 'import', 'settings', 'profile', 'themes', 'privacy', 'support'
  const [activeRecipePanel, setActiveRecipePanel] = useState(null); // 'recipes', 'quick-shopping-list', 'quick-todo-list'

  // Share Join Handler State
  const [shareToken, setShareToken] = useState(null);
  const [sharedListId, setSharedListId] = useState(null); // ID of list to open after joining
  
  // Data Backup State
  const [showSyncDialog, setShowSyncDialog] = useState(false);
  const [backupManager, setBackupManager] = useState(null);
  
  // App State (v0.6.0)
  const [activeTab, setActiveTab] = useState('bookmarks');
  const [premiumUser] = useState(true); // Temporary: unlock all premium features

  // Navigation Configuration
  const [navbarConfig, setNavbarConfig] = useState(() => {
    try {
      const saved = localStorage.getItem('navbarConfig');
      return saved ? JSON.parse(saved) : ['bookmarks', 'quick-shopping-list', 'recipes'];
    } catch {
      return ['bookmarks', 'quick-shopping-list', 'recipes'];
    }
  });

  // Handle navbar config changes from Settings
  const handleNavbarConfigChange = (newConfig) => {
    setNavbarConfig(newConfig);
  };
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeFeature, setUpgradeFeature] = useState('');
  
  // Infinite Scroll State - start with 15 items to fill larger screens
  const [displayCount, setDisplayCount] = useState(15);
  const BOOKMARKS_PER_LOAD = 10;
  const loadMoreRef = useRef(null);
  const bookmarksContainerRef = useRef(null);
  
  // Search & Filter State
  const [searchTerm, setSearchTerm] = useState('');
  // Drei Sucheingaben existieren parallel im DOM (unified/mobile/desktop-Layout,
  // je nach aktivem Theme per CSS ein-/ausgeblendet - siehe CustomThemes.css).
  // Eigene Refs pro Variante, Fokus geht beim Öffnen an die jeweils sichtbare.
  const unifiedSearchInputRef = useRef(null);
  const mobileSearchInputRef = useRef(null);
  const desktopSearchInputRef = useRef(null);
  const [selectedBookmarks, setSelectedBookmarks] = useState(new Set());
  const [isMultiSelectMode, setIsMultiSelectMode] = useState(false);
  
  // Import State
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importLoading, setImportLoading] = useState(false);

  // Cursor beim Wechsel auf "Lesezeichen" sofort ins Suchfeld setzen (v.a. für
  // Smartphones: sofort lostippen können, ohne erst das Feld antippen zu
  // müssen). Nur die aktuell sichtbare der drei Varianten wird tatsächlich
  // fokussiert - .focus() auf einem display:none-Element ist ein No-Op.
  useEffect(() => {
    if (activeTab !== 'bookmarks' || activeRecipePanel || activePanel || showForm) return;
    const timer = setTimeout(() => {
      [unifiedSearchInputRef.current, mobileSearchInputRef.current, desktopSearchInputRef.current]
        .find(el => el && el.offsetParent !== null)
        ?.focus();
    }, 50);
    return () => clearTimeout(timer);
  }, [activeTab, activeRecipePanel, activePanel, showForm]);

  // Initialize Enhanced Error Tracking
  useEffect(() => {
    errorTrackingService.initialize();
    // no console output
  }, []);

  // Set user context for error tracking
  useEffect(() => {
    if (user?.id) {
      errorTrackingService.setUserId(user.id);
    }
  }, [user?.id]);

  // 🧭 ONBOARDING: zeigen, solange der Server für diesen Nutzer
  // onboardingCompleted === false meldet (Feld kommt aus register()/login()/verify()).
  useEffect(() => {
    setShowOnboarding(user?.onboardingCompleted === false);
  }, [user?.onboardingCompleted]);

  const handleOnboardingFinish = useCallback(async () => {
    setShowOnboarding(false);
    await completeOnboarding();
  }, [completeOnboarding]);

  // Initialize backup system (respektiert den "Automatische Datensicherung"-Schalter aus den Einstellungen/Onboarding)
  useEffect(() => {
    let autoBackupEnabled = true;
    try {
      autoBackupEnabled = localStorage.getItem('autoBackup') !== 'false';
    } catch {
      // localStorage nicht verfügbar - Standard: aktiviert
    }
    if (user?.email && !backupManager && autoBackupEnabled) {
      const backup = setupAutoBackup(user.email);
      if (backup) {
        setBackupManager(backup.backupManager);
      }
    }
  }, [user?.email, backupManager]);

  // Check for share token in URL on mount and handle deep links
  useEffect(() => {
    const checkShareUrl = () => {
      const path = window.location.pathname;
      const shareMatch = path.match(/\/share\/shopping\/([a-zA-Z0-9]+)/);
      if (shareMatch) {
        setShareToken(shareMatch[1]);
        // Clean up URL without reload
        window.history.replaceState({}, '', '/');
      }
    };

    // Check on mount
    checkShareUrl();

    // Also check if there's a stored token after login
    const storedToken = sessionStorage.getItem('shareReturnToken');
    if (storedToken && isAuthenticated) {
      setShareToken(storedToken);
      sessionStorage.removeItem('shareReturnToken');
    }

    // Listen for Capacitor deep links (Android/iOS)
    const handleAppUrlOpen = async () => {
      try {
        const { App: CapacitorApp } = await import('@capacitor/app');
        CapacitorApp.addListener('appUrlOpen', (event) => {
          const url = new URL(event.url);
          if (url.pathname.startsWith('/share/shopping/')) {
            const token = url.pathname.split('/').pop();
            setShareToken(token);
          }
        });
      } catch (e) {
        // Capacitor not available (web)
      }
    };
    handleAppUrlOpen();
  }, [isAuthenticated]);

  // Handle share join success
  const handleShareJoinSuccess = (listId) => {
    setShareToken(null);
    // Store the list ID to show in QuickShoppingList
    if (listId) {
      setSharedListId(listId);
    }
    // Open QuickShoppingList with the shared list
    setActiveRecipePanel('quick-shopping-list');
  };

  // Handle share close
  const handleShareClose = () => {
    setShareToken(null);
  };

  // Recipe overlay handler
  const openRecipePanel = (panel) => {
    setActiveRecipePanel(panel);
    setActiveTab(null); // Clear tab to show recipe panel as active in navbar
    setActivePanel(null); // Close any open panel
  };

  const closeRecipePanel = () => {
    setActiveRecipePanel(null);
    setActiveTab('bookmarks'); // Always return to bookmarks
    window.location.hash = ''; // Clear hash
  };

  // Register Bookmark API Handler for Offline Queue
  useEffect(() => {
    offlineQueueService.registerApiHandler(EntityType.BOOKMARK, {
      create: (payload) => bookmarkApi.createBookmark(payload),
      update: (id, payload) => bookmarkApi.updateBookmark(id, payload),
      delete: (id) => bookmarkApi.deleteBookmark(id),
      getById: (id) => bookmarkApi.getBookmarkById(id)
    });

    // Cleanup orphaned queue items on app start
    offlineQueueService.cleanupOrphanedItems().then(removedCount => {
      if (removedCount > 0) {
        logger.debug(`[App] Cleaned up ${removedCount} orphaned queue items`);
      }
    }).catch(err => {
      console.warn('[App] Failed to cleanup orphaned items:', err);
    });

    // Subscribe to queue events to update temp IDs after sync
    const unsubscribe = offlineQueueService.subscribe((event) => {
      if (event.type === 'ITEM_SYNCED' && event.item?.entityType === EntityType.BOOKMARK) {
        const { tempId } = event.item;
        const realId = event.result?.id;

        if (tempId && realId && event.item.operation === OperationType.CREATE) {
          // Replace temp bookmark with real one
          setBookmarks(prev => prev.map(b =>
            b.id === tempId ? { ...event.result, _isTemp: false } : b
          ));
          logger.debug('[Bookmarks] Temp ID replaced:', tempId, '->', realId);
        }
      }
    });

    return () => unsubscribe();
  }, []);

  // Initialize App - same as before
  useEffect(() => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }

    const initializeApp = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const isApiHealthy = await bookmarkApi.checkHealth();
        logger.debug('🔍 Health check result:', isApiHealthy);
        setApiAvailable(isApiHealthy);
        
        if (isApiHealthy) {
          try {
            await loadBookmarksFromApi();
            // Check for data conflicts after loading
            setTimeout(() => checkForDataConflicts(), 1000);
          } catch (apiError) {
            // If API is healthy but bookmarks fail (likely auth), don't go offline
            logger.debug('📝 API is healthy but bookmarks require auth - this is normal');
            if (apiError.message && apiError.message.includes('403')) {
              // 403 is expected when not authenticated - continue normally
              logger.debug('✅ Backend is available, user needs to login');
            } else {
              throw apiError; // Re-throw other errors
            }
          }
        } else {
          await loadBookmarksFromLocalStorage();
        }
      } catch (err) {
        console.error('❌ Failed to initialize app:', err);
        console.error('❌ Error details:', err.message, err.stack);
        setError('Failed to connect to server. Using offline mode.');
        setApiAvailable(false);
        await loadBookmarksFromLocalStorage();
      } finally {
        setLoading(false);
      }
    };

    initializeApp();
  }, [isAuthenticated]);

  // Automatic backend connection checking every 5 minutes
  useEffect(() => {
    const checkConnectionStatus = async () => {
      try {
        // Use basic API endpoint check instead of health endpoint due to mail service issues
        const response = await fetch(`${APP_CONFIG.getBookmarkApiUrl()}`, {
          method: 'HEAD',
          headers: {
            'Accept': 'application/json'
          }
        });
        // 403 or 401 means backend is running but needs auth, which is good
        // 500+ means real problems
        setApiAvailable(response.status < 500);
        logger.debug('Backend status check:', response.status, response.status < 500 ? 'OK' : 'ERROR');
      } catch (error) {
        logger.debug('Backend connection check failed:', error);
        setApiAvailable(false);
      }
    };

    // Check immediately regardless of authentication status
    checkConnectionStatus();

    // Set up interval to check every 5 minutes (300000ms)
    const connectionInterval = setInterval(() => {
      checkConnectionStatus();
    }, 300000);

    // Cleanup interval on unmount
    return () => clearInterval(connectionInterval);
  }, []);

  // Helper: basic URL syntax validation (no network)
  const isInvalidUrl = (url) => {
    if (!url || typeof url !== 'string') return true;
    try {
      const u = new URL(url);
      return !(u.protocol === 'http:' || u.protocol === 'https:');
    } catch (_) {
      return true;
    }
  };

  const autoRemoveInvalidLinksEnabled = () => localStorage.getItem('autoRemoveInvalidLinks') === 'true';

  const removeInvalidBookmarks = async (list) => {
    const invalid = list.filter(b => isInvalidUrl(b.url));
    if (invalid.length === 0) return list;

    const valid = list.filter(b => !isInvalidUrl(b.url));
    try {
      if (apiAvailable) {
        // Best-effort batch delete; fallback to individual
        try {
          await bookmarkApi.deleteMultipleBookmarks(invalid.map(b => b.id));
        } catch (batchErr) {
          for (const b of invalid) {
            try { await bookmarkApi.deleteBookmark(b.id); } catch (_) { /* ignore */ }
          }
        }
      }
    } catch (_) {
      // ignore API errors for cleanup
    }

    // Inform user briefly
    setError(`✅ ${invalid.length} ungültige Links entfernt`);
    setTimeout(() => setError(null), 3000);
    return valid;
  };

  // Load bookmarks from API
  const loadBookmarksFromApi = async () => {
    try {
      const data = await bookmarkApi.getAllBookmarks();
      // Apply migrations BEFORE setting to state
      let migratedData = data && data.length > 0 ? applyMigrationSync(data) : data;
      migratedData = migratedData && migratedData.length > 0 ? applyRatingMigration(migratedData) : migratedData;

      // Auto-remove invalid links if enabled
      if (autoRemoveInvalidLinksEnabled() && migratedData && migratedData.length > 0) {
        migratedData = await removeInvalidBookmarks(migratedData);
      }

      // Preserve temp bookmarks that haven't been synced yet
      let existingTempBookmarks = [];
      try {
        const cachedBookmarks = await offlineDb.getAll(STORES.BOOKMARKS);
        existingTempBookmarks = cachedBookmarks.filter(b => b._isTemp);
      } catch (idbErr) {
        console.warn('[Bookmarks] IndexedDB read failed:', idbErr);
      }

      // Merge: API data + temp bookmarks
      const finalBookmarks = [...(migratedData || []), ...existingTempBookmarks];
      setBookmarks(finalBookmarks);

      // Cache API data in IndexedDB (but keep temp bookmarks)
      try {
        // Clear non-temp bookmarks and re-add
        const cachedBookmarks = await offlineDb.getAll(STORES.BOOKMARKS);
        for (const cached of cachedBookmarks) {
          if (!cached._isTemp) {
            await offlineDb.delete(STORES.BOOKMARKS, cached.id);
          }
        }
        // Add fresh API data
        for (const bookmark of (migratedData || [])) {
          await offlineDb.put(STORES.BOOKMARKS, bookmark);
        }
      } catch (cacheErr) {
        console.warn('[Bookmarks] IndexedDB cache update failed:', cacheErr);
      }

      // Update backend with migrated data if needed
      if (migratedData && migratedData !== data) {
        updateBackendWithMigratedBookmarks(data, migratedData);
      }
    } catch (err) {
      console.error('Failed to load bookmarks from API:', err);
      throw err;
    }
  };

  // Load bookmarks from localStorage/IndexedDB (fallback)
  const loadBookmarksFromLocalStorage = async () => {
    if (!user?.email) return;

    // Try IndexedDB first (has offline-created bookmarks)
    let indexedDbBookmarks = [];
    try {
      indexedDbBookmarks = await offlineDb.getAll(STORES.BOOKMARKS);
      logger.debug('[Bookmarks] Loaded from IndexedDB:', indexedDbBookmarks.length);
    } catch (idbErr) {
      console.warn('[Bookmarks] IndexedDB load failed:', idbErr);
    }

    // Also check localStorage
    const userKey = `bookmarks_${user.email}`;
    const savedBookmarks = localStorage.getItem(userKey);
    let localStorageBookmarks = [];

    if (savedBookmarks) {
      try {
        localStorageBookmarks = JSON.parse(savedBookmarks);
      } catch (error) {
        console.error('Error parsing localStorage bookmarks:', error);
      }
    }

    // Merge: IndexedDB has priority (contains temp bookmarks)
    const mergedMap = new Map();

    // Add localStorage bookmarks first
    localStorageBookmarks.forEach(b => mergedMap.set(b.id, b));

    // Override/add with IndexedDB bookmarks
    indexedDbBookmarks.forEach(b => mergedMap.set(b.id, b));

    let bookmarksData = Array.from(mergedMap.values());

    if (bookmarksData.length > 0) {
      // Apply migrations BEFORE setting bookmarks to state
      let migratedBookmarks = applyMigrationSync(bookmarksData);
      migratedBookmarks = applyRatingMigration(migratedBookmarks);

      // Auto-remove invalid links if enabled (offline/local)
      if (autoRemoveInvalidLinksEnabled() && migratedBookmarks && migratedBookmarks.length > 0) {
        const invalid = migratedBookmarks.filter(b => isInvalidUrl(b.url));
        if (invalid.length > 0) {
          migratedBookmarks = migratedBookmarks.filter(b => !isInvalidUrl(b.url));
          setError(`✅ ${invalid.length} ungültige Links entfernt`);
          setTimeout(() => setError(null), 3000);
        }
      }

      setBookmarks(migratedBookmarks);
      localStorage.setItem(userKey, JSON.stringify(migratedBookmarks));
    } else {
      setBookmarks([]);
    }
  };

  // Synchronous migration function to fix bookmarks with undefined categories
  const applyMigrationSync = (bookmarksToCheck) => {
    const bookmarksNeedingMigration = bookmarksToCheck.filter(b => 
      b.category === undefined || b.category === null
    );
    
    if (bookmarksNeedingMigration.length > 0) {
      logger.debug('🔧 SYNCHRONOUS MIGRATION: Fixing bookmarks with undefined categories:', bookmarksNeedingMigration);
      
      const migratedBookmarks = bookmarksToCheck.map(bookmark => {
        if (bookmark.category === undefined || bookmark.category === null) {
          // Special case: Hof-Bartels bookmark should be in "Ausflüge" category
          if (bookmark.url && bookmark.url.includes('hof-bartels')) {
            logger.debug('🏞️ Assigning Hof-Bartels bookmark to "Ausflüge" category');
            return { ...bookmark, category: 'Ausflüge' };
          }
          // Default: assign empty string for "Ohne Kategorie"
          logger.debug(`📝 Assigning "${bookmark.title}" to empty category`);
          return { ...bookmark, category: '' };
        }
        return bookmark;
      });
      
      logger.debug('✅ SYNCHRONOUS MIGRATION completed:', migratedBookmarks.map(b => ({ title: b.title, category: b.category })));
      return migratedBookmarks;
    }
    
    logger.debug('ℹ️ No migration needed - all bookmarks have categories');
    return bookmarksToCheck;
  };

  // Rating Migration: Convert isFavorite (boolean) to rating (0-4)
  const applyRatingMigration = (bookmarksToCheck) => {
    const needsMigration = bookmarksToCheck.some(b =>
      typeof b.rating === 'undefined' && typeof b.isFavorite !== 'undefined'
    );

    if (needsMigration) {
      logger.debug('🔧 Migrating isFavorite → rating system');
      return bookmarksToCheck.map(bookmark => {
        if (typeof bookmark.rating === 'number') {
          return bookmark;
        }

        const rating = bookmark.isFavorite === true ? 1 : 0;
        const { isFavorite, ...rest } = bookmark;
        return { ...rest, rating };
      });
    }

    return bookmarksToCheck;
  };

  // Update backend with migrated bookmarks
  const updateBackendWithMigratedBookmarks = async (originalBookmarks, migratedBookmarks) => {
    if (!apiAvailable) return;
    
    try {
      for (let i = 0; i < originalBookmarks.length; i++) {
        const original = originalBookmarks[i];
        const migrated = migratedBookmarks[i];
        
        if (original.category !== migrated.category) {
          await bookmarkApi.updateBookmark(original.id, migrated);
          logger.debug(`✅ Updated bookmark ${original.id} in backend with category: "${migrated.category}"`);
        }
      }
    } catch (error) {
      console.warn('⚠️ Failed to update migrated bookmarks in backend:', error);
    }
  };

  // Save bookmarks to localStorage (backup) and auto-backup
  useEffect(() => {
    if (user?.email && (bookmarks.length > 0 || !apiAvailable)) {
      const userKey = `bookmarks_${user.email}`;
      localStorage.setItem(userKey, JSON.stringify(bookmarks));
      
      // Auto-backup with DataBackupManager
      if (backupManager?.autoSave && bookmarks.length > 0) {
        backupManager.autoSave(bookmarks);
      }
    }
  }, [bookmarks, apiAvailable, user, backupManager]);

  // Check for data conflicts between local and backend
  const checkForDataConflicts = async () => {
    if (!user?.email || !backupManager || !apiAvailable) return;
    
    try {
      const localData = backupManager.loadLocalBackup();
      const backendData = await bookmarkApi.getAllBookmarks();
      
      const comparison = backupManager.compareData(localData.bookmarks, backendData);
      
      // Show sync dialog if there are conflicts or data loss detected
      if (comparison.dataLoss || (comparison.onlyInLocal > 0 && comparison.needsSync)) {
        setShowSyncDialog(true);
      }
    } catch (error) {
      console.error('Failed to check data conflicts:', error);
    }
  };

  // Handle sync completion
  const handleSyncComplete = async (syncedCount) => {
    setShowSyncDialog(false);
    
    if (syncedCount > 0) {
      // Reload bookmarks to show synced data
      try {
        await loadBookmarksFromApi();
      } catch (error) {
        console.error('Failed to reload after sync:', error);
      }
    }
  };

  // Tab Navigation Handler
  const handleTabChange = (tabId, lockedFeature = null) => {
    if (tabId === 'upgrade') {
      setUpgradeFeature(lockedFeature);
      setShowUpgradeModal(true);
      return;
    }
    setActiveTab(tabId);
    setActiveRecipePanel(null); // Close recipe panel when switching to tab
    setActivePanel(null); // Close any open panel
    setShowForm(false); // Close form when switching tabs
  };

  // Panel open handler - routes 'bookmarks'/'reminders' to tab navigation
  const handleOpenPanel = (panelName) => {
    if (panelName === 'bookmarks' || panelName === 'reminders') {
      setActiveTab(panelName);
      setActivePanel(null);
      setActiveRecipePanel(null);
      setShowForm(false);
    } else {
      // Close any open recipe panel when opening a settings panel
      setActiveRecipePanel(null);
      setActivePanel(panelName);
    }
  };

  // Manual sync trigger (for testing)
  const triggerDataSync = () => {
    if (backupManager && user?.email) {
      checkForDataConflicts();
    }
  };

  // Add Bookmark (with Offline Queue Support)
  const addBookmark = async (bookmarkData, options = {}) => {
    try {
      setError(null);

      // Generate temp ID for offline/optimistic update
      const tempId = `temp-bookmark-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      const tempBookmark = {
        id: tempId,
        ...bookmarkData,
        rating: bookmarkData.rating || 0,
        createdAt: new Date().toISOString(),
        category: bookmarkData.category || '',
        _isTemp: true
      };

      // Optimistic Update - add immediately to UI
      setBookmarks(prev => [...prev, tempBookmark]);

      // Sicherheits-Snapshot beim Anlegen (nur wenn "Automatische Datensicherung" aktiv ist).
      // Wird bei einer Wiederherstellung aus dem Archiv übersprungen (skipArchiveSnapshot),
      // damit das Archiv nicht bei jedem Wiederherstellen einen neuen Eintrag anlegt.
      // Bewusst nicht awaitet (fire-and-forget) - das Anlegen des Bookmarks
      // soll nicht auf das Archivieren warten müssen; archiveItem() fängt
      // Fehler intern ab und blockiert nie die eigentliche Aktion.
      if (!options.skipArchiveSnapshot) {
        archiveItem(
          ENTITY_TYPES.BOOKMARK,
          ARCHIVE_REASONS.AUTO_SNAPSHOT,
          tempBookmark,
          { label: tempBookmark.title }
        );
      }

      // Cache in IndexedDB
      try {
        await offlineDb.put(STORES.BOOKMARKS, tempBookmark);
      } catch (cacheErr) {
        console.warn('[Bookmarks] IndexedDB cache failed:', cacheErr);
      }

      if (apiAvailable && navigator.onLine) {
        // Online: Try direct API call
        try {
          const newBookmark = await bookmarkApi.createBookmark(bookmarkData);
          // Replace temp bookmark with real one
          setBookmarks(prev => prev.map(b =>
            b.id === tempId ? { ...newBookmark, _isTemp: false } : b
          ));
          // Update cache with real ID
          await offlineDb.delete(STORES.BOOKMARKS, tempId);
          await offlineDb.put(STORES.BOOKMARKS, newBookmark);
        } catch (apiErr) {
          console.warn('[Bookmarks] API failed, queueing for later:', apiErr);
          // Queue for later sync
          await offlineQueueService.enqueue({
            entityType: EntityType.BOOKMARK,
            operation: OperationType.CREATE,
            tempId: tempId,
            payload: bookmarkData
          });
        }
      } else {
        // Offline: Queue the operation
        await offlineQueueService.enqueue({
          entityType: EntityType.BOOKMARK,
          operation: OperationType.CREATE,
          tempId: tempId,
          payload: bookmarkData
        });
      }

      setShowForm(false);
    } catch (err) {
      console.error('Failed to add bookmark:', err);
      setError(`Failed to add bookmark: ${err.message}`);
    }
  };

  // Trägt ein bereits vom Server bekanntes Lesezeichen in den lokalen State
  // ein, OHNE einen weiteren Backend-Call zu machen. Wird von Archive.js nach
  // einem erfolgreichen POST /api/archive/{id}/restore aufgerufen - das
  // Anlegen selbst hat das Backend beim Restore bereits übernommen, ein
  // erneuter addBookmark()-Aufruf würde das Lesezeichen doppelt anlegen.
  const addRestoredBookmark = async (bookmarkObj) => {
    if (!bookmarkObj) return;
    setBookmarks(prev => [...prev, bookmarkObj]);
    try {
      await offlineDb.put(STORES.BOOKMARKS, bookmarkObj);
    } catch (cacheErr) {
      console.warn('[Bookmarks] IndexedDB cache failed:', cacheErr);
    }
  };

  // Update Bookmark (with Offline Queue Support)
  const updateBookmark = async (id, updatedData) => {
    logger.debug('🔄 updateBookmark called:', { id, updatedData });
    try {
      setError(null);

      // Get original bookmark for conflict detection
      const originalBookmark = bookmarks.find(b => b.id === id);
      const updatedBookmark = {
        ...originalBookmark,
        ...updatedData,
        updatedAt: new Date().toISOString()
      };

      // Optimistic Update
      setBookmarks(prev => prev.map(bookmark =>
        bookmark.id === id ? updatedBookmark : bookmark
      ));

      // Cache in IndexedDB
      try {
        await offlineDb.put(STORES.BOOKMARKS, updatedBookmark);
      } catch (cacheErr) {
        console.warn('[Bookmarks] IndexedDB cache failed:', cacheErr);
      }

      // Skip API for temp bookmarks (they're already queued)
      if (String(id).startsWith('temp-')) {
        logger.debug('📝 Temp bookmark update - only updating local state');
        setEditingBookmark(null);
        setShowForm(false);
        setSelectedBookmarks(new Set());
        return;
      }

      if (apiAvailable && navigator.onLine) {
        try {
          logger.debug('📡 Updating bookmark via API...');
          const result = await bookmarkApi.updateBookmark(id, updatedData);
          logger.debug('✅ API response:', result);
          setBookmarks(prev => prev.map(bookmark =>
            bookmark.id === id ? result : bookmark
          ));
          await offlineDb.put(STORES.BOOKMARKS, result);
        } catch (apiErr) {
          console.warn('[Bookmarks] API failed, queueing for later:', apiErr);
          await offlineQueueService.enqueue({
            entityType: EntityType.BOOKMARK,
            operation: OperationType.UPDATE,
            entityId: id,
            payload: updatedData,
            originalUpdatedAt: originalBookmark?.updatedAt
          });
        }
      } else {
        logger.debug('💾 Offline mode - queueing update...');
        await offlineQueueService.enqueue({
          entityType: EntityType.BOOKMARK,
          operation: OperationType.UPDATE,
          entityId: id,
          payload: updatedData,
          originalUpdatedAt: originalBookmark?.updatedAt
        });
      }

      setEditingBookmark(null);
      setShowForm(false);
      setSelectedBookmarks(new Set());
      logger.debug('✅ Bookmark update completed successfully');
    } catch (err) {
      console.error('❌ Failed to update bookmark:', err);
      setError(`Failed to update bookmark: ${err.message}`);
    }
  };

  // Stellt alle Lesezeichen einer umbenannten Kategorie auf den neuen Namen
  // um. Nötig, weil ein Kategorien-Rename in CategoryContext.updateCategory()
  // als "neu anlegen + alte löschen" nachgebildet wird (kein PUT/PATCH-
  // Endpoint für Kategorien im Backend) - Lesezeichen speichern ihre
  // Kategorie als Namens-String, würden also ohne diesen Schritt an der
  // gelöschten alten Kategorie hängen bleiben und aus der UI verschwinden.
  // Läuft sequentiell (kein Batch-Update-Endpoint im Backend), pro
  // Lesezeichen einzeln fehlertolerant über dieselbe Offline-Queue wie
  // updateBookmark.
  const renameBookmarksCategory = async (oldName, newName) => {
    const affected = bookmarks.filter(bookmark => {
      if (!bookmark.category) return false;
      return stripCategoryPrefix(String(bookmark.category).trim()) === stripCategoryPrefix(String(oldName).trim());
    });

    for (const bookmark of affected) {
      const updatedBookmark = { ...bookmark, category: newName, updatedAt: new Date().toISOString() };

      // Optimistic Update
      setBookmarks(prev => prev.map(b => (b.id === bookmark.id ? updatedBookmark : b)));

      try {
        await offlineDb.put(STORES.BOOKMARKS, updatedBookmark);
      } catch (cacheErr) {
        console.warn('[Bookmarks] IndexedDB cache failed:', cacheErr);
      }

      // Temp-Lesezeichen werden ohnehin noch über die Queue synchronisiert -
      // dort greift der aktualisierte lokale State bereits.
      if (String(bookmark.id).startsWith('temp-')) {
        continue;
      }

      if (apiAvailable && navigator.onLine) {
        try {
          const result = await bookmarkApi.updateBookmark(bookmark.id, updatedBookmark);
          setBookmarks(prev => prev.map(b => (b.id === bookmark.id ? result : b)));
          await offlineDb.put(STORES.BOOKMARKS, result);
        } catch (apiErr) {
          console.warn(`[Bookmarks] Kategorie-Update für Lesezeichen ${bookmark.id} fehlgeschlagen, wird nachgeholt:`, apiErr);
          await offlineQueueService.enqueue({
            entityType: EntityType.BOOKMARK,
            operation: OperationType.UPDATE,
            entityId: bookmark.id,
            payload: updatedBookmark,
            originalUpdatedAt: bookmark.updatedAt
          });
        }
      } else {
        await offlineQueueService.enqueue({
          entityType: EntityType.BOOKMARK,
          operation: OperationType.UPDATE,
          entityId: bookmark.id,
          payload: updatedBookmark,
          originalUpdatedAt: bookmark.updatedAt
        });
      }
    }
  };

  // Delete Bookmark (with Offline Queue Support)
  const deleteBookmark = async (id) => {
    if (window.confirm(t('deleteConfirm'))) {
      try {
        setError(null);

        // Papierkorb: gelöschtes Lesezeichen immer archivieren, damit es wiederherstellbar
        // bleibt - awaited, damit das Archivieren VOR dem eigentlichen Löschen abgeschlossen ist
        const bookmarkToArchive = bookmarks.find(bookmark => bookmark.id === id);
        if (bookmarkToArchive) {
          await archiveItem(
            ENTITY_TYPES.BOOKMARK,
            ARCHIVE_REASONS.DELETED,
            bookmarkToArchive,
            { label: bookmarkToArchive.title }
          );
        }

        // Optimistic Update - remove immediately from UI
        setBookmarks(prev => prev.filter(bookmark => bookmark.id !== id));

        // Remove from IndexedDB cache
        try {
          await offlineDb.delete(STORES.BOOKMARKS, id);
        } catch (cacheErr) {
          console.warn('[Bookmarks] IndexedDB delete failed:', cacheErr);
        }

        // If it's a temp bookmark, no need to call API
        if (String(id).startsWith('temp-')) {
          logger.debug('📝 Temp bookmark deleted - no API call needed');
          return;
        }

        if (apiAvailable && navigator.onLine) {
          try {
            await bookmarkApi.deleteBookmark(id);
          } catch (apiErr) {
            console.warn('[Bookmarks] API delete failed, queueing:', apiErr);
            await offlineQueueService.enqueue({
              entityType: EntityType.BOOKMARK,
              operation: OperationType.DELETE,
              entityId: id
            });
          }
        } else {
          // Offline: Queue the delete
          await offlineQueueService.enqueue({
            entityType: EntityType.BOOKMARK,
            operation: OperationType.DELETE,
            entityId: id
          });
        }
      } catch (err) {
        console.error('Failed to delete bookmark:', err);
        setError(`Failed to delete bookmark: ${err.message}`);
      }
    }
  };

  // Handle Form Actions
  const startEdit = (bookmark) => {
    logger.debug('✏️ startEdit called with bookmark:', bookmark);
    setEditingBookmark(bookmark);
    setShowForm(true);
    logger.debug('✅ startEdit: Set editingBookmark and showForm=true');
  };

  const cancelForm = () => {
    setEditingBookmark(null);
    setShowForm(false);
  };

  const handleAddBookmark = () => {
    setEditingBookmark(null);
    setShowForm(true);
  };

  // Handle Mode Change
  const handleModeChange = (isOnline) => {
    setForceOnlineMode(isOnline);
  };

  // Handle Import
  const handleImportClick = () => {
    setShowImportDialog(true);
  };

  const handleImport = async (bookmarks, stats) => {
    setImportLoading(true);
    setError(null);

    try {
      logger.debug(`📥 Importing ${bookmarks.length} bookmarks...`);
      
      let result;
      
      if (apiAvailable) {
        result = await importApi.importBookmarks(bookmarks);
        await loadBookmarksFromApi();
      } else {
        const importedBookmarks = bookmarks.map(bookmark => ({
          id: Date.now() + Math.random(),
          ...bookmark,
          createdAt: new Date().toISOString()
        }));
        
        setBookmarks(prev => [...prev, ...importedBookmarks]);
        
        result = {
          success: true,
          imported: importedBookmarks.length,
          message: `${importedBookmarks.length} Lesezeichen lokal importiert`
        };
      }
      
      if (user?.email) {
        await importApi.saveImportToDirectory(user.email, bookmarks, 'chrome_import.html');
      }
      
      setError(`✅ ${result.message || 'Import erfolgreich!'} (${result.imported} Lesezeichen)`);
      
      setTimeout(() => {
        setError(null);
      }, 5000);
      
    } catch (err) {
      console.error('❌ Import failed:', err);
      setError(`Import fehlgeschlagen: ${err.message}`);
    } finally {
      setImportLoading(false);
    }
  };

  // Voice functions moved to HamburgerMenu

  // Parse star rating from search term
  // Supports multiple languages: DE, EN, FR, ES, IT
  // Examples: "2 sterne", "3 stars", "3 étoiles", "3 estrellas", "3 stelle", "4⭐"
  const parseStarSearch = (term) => {
    // Combined pattern for all languages:
    // DE: sterne/stern, EN: stars/star, FR: étoiles/étoile, ES: estrellas/estrella, IT: stelle/stella
    const starPattern = /^(\d)\s*(?:sterne?|stars?|étoiles?|estrellas?|stelle?|⭐)\s*(.*)$/i;

    const match = term.match(starPattern);
    if (match) {
      return {
        rating: parseInt(match[1], 10),
        remainingTerm: match[2]?.trim() || ''
      };
    }
    return null;
  };

  // Filter bookmarks for display
  const getFilteredBookmarks = () => {
    let filtered = bookmarks;

    if (searchTerm) {
      const term = searchTerm.toLowerCase().trim();
      const starSearch = parseStarSearch(term);

      if (starSearch) {
        // Filter by star rating
        filtered = filtered.filter(bookmark =>
          (bookmark.rating || 0) === starSearch.rating
        );

        // Additionally filter by remaining search term if provided
        if (starSearch.remainingTerm) {
          const remainingTerm = starSearch.remainingTerm.toLowerCase();
          filtered = filtered.filter(bookmark =>
            bookmark.title?.toLowerCase().includes(remainingTerm) ||
            bookmark.url?.toLowerCase().includes(remainingTerm) ||
            bookmark.description?.toLowerCase().includes(remainingTerm) ||
            bookmark.category?.toLowerCase().includes(remainingTerm)
          );
        }
      } else {
        // Standard text search
        filtered = filtered.filter(bookmark =>
          bookmark.title?.toLowerCase().includes(term) ||
          bookmark.url?.toLowerCase().includes(term) ||
          bookmark.description?.toLowerCase().includes(term) ||
          bookmark.category?.toLowerCase().includes(term)
        );
      }
    }

    return filtered.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  };

  // Get displayed bookmarks for infinite scroll
  const getDisplayedBookmarks = () => {
    const filtered = getFilteredBookmarks();
    return filtered.slice(0, displayCount);
  };

  // Check if there are more bookmarks to load
  const hasMoreBookmarks = () => {
    return displayCount < getFilteredBookmarks().length;
  };

  // Load more bookmarks
  const loadMoreBookmarks = useCallback(() => {
    if (hasMoreBookmarks()) {
      setDisplayCount(prev => prev + BOOKMARKS_PER_LOAD);
    }
  }, [displayCount]);

  // Reset display count when search changes
  useEffect(() => {
    setDisplayCount(15);
  }, [searchTerm]);

  // Infinite scroll with onScroll handler
  const handleBookmarksScroll = useCallback((e) => {
    const container = e.target;
    const { scrollTop, scrollHeight, clientHeight } = container;

    // Load more when user scrolls to bottom (with 100px threshold)
    if (scrollHeight - scrollTop - clientHeight < 100) {
      const filtered = getFilteredBookmarks();
      if (displayCount < filtered.length) {
        setDisplayCount(prev => Math.min(prev + BOOKMARKS_PER_LOAD, filtered.length));
      }
    }
  }, [displayCount, bookmarks, searchTerm]);

  // Multi-select functions
  const toggleBookmarkSelection = (bookmarkId) => {
    setSelectedBookmarks(prev => {
      const newSelection = new Set(prev);
      if (newSelection.has(bookmarkId)) {
        newSelection.delete(bookmarkId);
      } else {
        newSelection.add(bookmarkId);
      }
      return newSelection;
    });
  };

  const selectAllBookmarks = () => {
    const visibleBookmarkIds = getDisplayedBookmarks().map(b => b.id);
    setSelectedBookmarks(new Set(visibleBookmarkIds));
  };

  const deselectAllBookmarks = () => {
    setSelectedBookmarks(new Set());
  };

  const bulkAssignCategory = async (categoryName) => {
    const bookmarkIds = Array.from(selectedBookmarks);
    
    for (const id of bookmarkIds) {
      try {
        const updatedData = { category: categoryName };
        if (apiAvailable) {
          await bookmarkApi.updateBookmark(id, updatedData);
        }
        
        setBookmarks(prev => 
          prev.map(bookmark => 
            bookmark.id === id 
              ? { ...bookmark, category: categoryName }
              : bookmark
          )
        );
      } catch (err) {
        console.error(`Failed to update bookmark ${id}:`, err);
      }
    }
    
    setSelectedBookmarks(new Set());
    setIsMultiSelectMode(false);
  };

  const deleteSelectedBookmarks = async () => {
    const bookmarkIds = Array.from(selectedBookmarks);
    const bookmarkCount = bookmarkIds.length;
    
    if (bookmarkCount === 0) return;
    
    const confirmMessage = bookmarkCount === 1 
      ? t('deleteConfirm') || 'Sind Sie sicher, dass Sie dieses Lesezeichen löschen möchten?'
      : `Sind Sie sicher, dass Sie ${bookmarkCount} Lesezeichen löschen möchten?`;
    
    if (window.confirm(confirmMessage)) {
      try {
        setError(null);
        
        // Delete from backend if available
        if (apiAvailable) {
          try {
            const result = await bookmarkApi.deleteMultipleBookmarks(bookmarkIds);
            logger.debug(`✅ Batch deleted ${result.deleted} of ${result.requested} bookmarks from backend`);
          } catch (err) {
            console.error(`❌ Failed to batch delete bookmarks from backend:`, err);
            // Fallback to individual deletion
            for (const id of bookmarkIds) {
              try {
                await bookmarkApi.deleteBookmark(id);
                logger.debug(`✅ Fallback: Deleted bookmark ${id} from backend`);
              } catch (fallbackErr) {
                console.error(`❌ Fallback failed for bookmark ${id}:`, fallbackErr);
              }
            }
          }
        }
        
        // Remove from local state
        setBookmarks(prev => prev.filter(bookmark => !bookmarkIds.includes(bookmark.id)));
        
        // Reset selection
        setSelectedBookmarks(new Set());
        setIsMultiSelectMode(false);
        
        logger.debug(`🗑️ Successfully deleted ${bookmarkCount} bookmark(s)`);
        
      } catch (err) {
        console.error('Failed to delete selected bookmarks:', err);
        setError(`Failed to delete bookmarks: ${err.message}`);
      }
    }
  };

  // Render Premium Upgrade Handler
  const handleUpgrade = () => {
    setShowUpgradeModal(true);
  };

  // Render Tab Content
  const renderTabContent = () => {
    // Handle recipe panels first (they can be shown with any tab or null)
    if (activeRecipePanel) {
      return (
        <div className="dynamic-content-form">
          {activeRecipePanel === 'recipes' && (
            <RecipesList
              isOpen={true}
              onClose={closeRecipePanel}
              bookmarks={bookmarks}
            />
          )}

          {activeRecipePanel === 'quick-shopping-list' && (
            <QuickShoppingList
              isOpen={true}
              onClose={closeRecipePanel}
              sharedListId={sharedListId}
              onClearSharedList={() => setSharedListId(null)}
            />
          )}

          {activeRecipePanel === 'quick-todo-list' && (
            <QuickTodoList
              isOpen={true}
              onClose={closeRecipePanel}
            />
          )}

        </div>
      );
    }

    // Handle settings panels (can be shown with any tab or null)
    if (activePanel) {
      return (
        <div className="dynamic-content-form">
          {activePanel === 'categories' && (
            <CategoryManagement
              key={`categories-${bookmarks.length}-${bookmarks.map(b => b.id + b.category).join('')}`}
              isOpen={true}
              onClose={() => setActivePanel(null)}
              bookmarks={bookmarks}
              onRenameCategory={renameBookmarksCategory}
            />
          )}

          {activePanel === 'export' && (
            <ExportDialog
              isOpen={true}
              onClose={() => setActivePanel(null)}
              bookmarks={bookmarks}
            />
          )}

          {activePanel === 'import' && (
            <ImportDialog
              isOpen={true}
              onClose={() => setActivePanel(null)}
              onImport={handleImport}
              loading={importLoading}
            />
          )}

          {activePanel === 'archive' && (
            <Archive
              isOpen={true}
              onClose={() => setActivePanel(null)}
              bookmarks={bookmarks}
              onRestoreBookmark={addRestoredBookmark}
            />
          )}

          {activePanel === 'themes' && (
            <ThemeModal
              isOpen={true}
              onClose={() => setActivePanel(null)}
            />
          )}

          {activePanel === 'privacy' && (
            <PrivacyPolicy
              isOpen={true}
              onClose={() => setActivePanel(null)}
            />
          )}

          {activePanel === 'settings' && (
            <Settings
              isOpen={true}
              onClose={() => setActivePanel(null)}
              onNavbarConfigChange={handleNavbarConfigChange}
            />
          )}

          {activePanel === 'profile' && (
            <Settings
              isOpen={true}
              section="profile"
              onClose={() => setActivePanel(null)}
              onNavbarConfigChange={handleNavbarConfigChange}
            />
          )}

          {activePanel === 'support' && (
            <Support
              isOpen={true}
              onClose={() => setActivePanel(null)}
            />
          )}

          {activePanel === 'version' && (
            <VersionInfo
              onClose={() => setActivePanel(null)}
            />
          )}
        </div>
      );
    }

    switch (activeTab) {
      case 'bookmarks':
        // Show bookmark form if it's active
        if (showForm) {
          logger.debug('📝 Rendering BookmarkForm:', { editingBookmark, showForm });
          return (
            <div className="dynamic-content-form">
              <BookmarkForm
                bookmark={editingBookmark}
                voiceData={null}
                onSubmit={editingBookmark ? updateBookmark : addBookmark}
                onCancel={cancelForm}
                onDelete={deleteBookmark}
                categories={categories}
              />
            </div>
          );
        }

        return (
          <div className="power-panel bookmarks-power-panel">
            {/* Unified Search Section - Purple Bubbles style (same as QuickShoppingList) */}
            <div className="unified-search-section search-with-add">
              <input
                ref={unifiedSearchInputRef}
                type="text"
                className="unified-search-input"
                placeholder={t('searchBookmarks') || 'Lesezeichen suchen...'}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <button
                  className="clear-search"
                  onClick={() => setSearchTerm('')}
                  title={t('clearSearch') || 'Suche löschen'}
                >
                  ×
                </button>
              )}
              <button
                className="search-add-btn"
                onClick={handleAddBookmark}
                title={t('addBookmark') || 'Neues Lesezeichen'}
                aria-label={t('addBookmark') || 'Neues Lesezeichen'}
              >
                +
              </button>
            </div>

            {/* Header - App Bar Style (same structure as QuickShoppingList) */}
            <div className="quick-list-header">
              <div className="quick-list-header-left"></div>
              <div className="header-center"></div>
              <div className="quick-list-header-actions">
                <button
                  className="quick-list-share-btn"
                  onClick={() => {
                    if (navigator.share) {
                      navigator.share({
                        title: t('menu.bookmarks') || 'Lesezeichen',
                        url: window.location.href
                      });
                    }
                  }}
                  title={t('share.shareList') || 'Teilen'}
                >
                  <FaShareAlt />
                </button>
              </div>
            </div>

            {/* Power Headline with Search (Mobile: Search only, Desktop: Title + Search below) */}
            <div className="power-headline">
              {/* Mobile: Search Bar inside headline */}
              <div className="mobile-search-container mobile-only">
                <div className="search-input-wrapper">
                  <span className="search-icon">🔍</span>
                  <input
                    ref={mobileSearchInputRef}
                    type="text"
                    className="search-input"
                    placeholder={t('searchBookmarks') || 'Suche... (z.B. "3 Sterne" oder "2 Sterne Kuchen")'}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  {searchTerm && (
                    <button
                      className="clear-search"
                      onClick={() => setSearchTerm('')}
                      title="Suche löschen"
                    >
                      ×
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Desktop: Search and Filters (hidden on mobile) */}
            <div className="search-filters-container desktop-only">
              <div className="search-bar-container">
                <div className="search-input-wrapper">
                  <span className="search-icon">🔍</span>
                  <input
                    ref={desktopSearchInputRef}
                    type="text"
                    className="search-input"
                    placeholder={t('searchBookmarks') || 'Suche... (z.B. "3 Sterne" oder "2 Sterne Kuchen")'}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  {searchTerm && (
                    <button
                      className="clear-search"
                      onClick={() => setSearchTerm('')}
                      title="Suche löschen"
                    >
                      ×
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Multi-select controls */}
            {getFilteredBookmarks().length > 0 && isMultiSelectMode && (
              <div className="multi-select-controls">
                <button className="filter-chip select-all-btn" onClick={selectAllBookmarks}>
                  ✅ Alle auswählen
                </button>
                
                <button 
                  className="filter-chip exit-multi-select-btn"
                  onClick={() => {
                    setIsMultiSelectMode(false);
                    setSelectedBookmarks(new Set());
                  }}
                >
                  ❌ Auswahl beenden
                </button>
                
                {selectedBookmarks.size > 0 && (
                  <>
                    <span className="filter-chip selection-count">{selectedBookmarks.size} ausgewählt</span>
                    <button 
                      className="filter-chip delete-selected-btn" 
                      onClick={deleteSelectedBookmarks}
                      title={`${selectedBookmarks.size} ausgewählte Bookmarks löschen`}
                    >
                      Löschen ({selectedBookmarks.size})
                    </button>
                  </>
                )}
              </div>
            )}

            {/* Bookmarks List */}
            {loading ? (
              <div className="loading-state">
                <div className="loading-spinner"></div>
                <p>Loading bookmarks...</p>
              </div>
            ) : (
              <>
                <div
                  className="bookmarks-container"
                  ref={bookmarksContainerRef}
                  onScroll={handleBookmarksScroll}
                >
                  {getDisplayedBookmarks().map(bookmark => (
                    <CompactBookmarkItem
                      key={bookmark.id}
                      bookmark={bookmark}
                      onEdit={startEdit}
                      onDelete={deleteBookmark}
                      premiumUser={premiumUser}
                      isMultiSelectMode={isMultiSelectMode}
                      isSelected={selectedBookmarks.has(bookmark.id)}
                      onToggleSelection={toggleBookmarkSelection}
                    />
                  ))}
                </div>

                {/* Bookmark count info */}
                {getFilteredBookmarks().length > 0 && (
                  <div className="pagination-info">
                    Zeige {getDisplayedBookmarks().length} von {getFilteredBookmarks().length} Lesezeichen
                  </div>
                )}

                {getFilteredBookmarks().length === 0 && !showForm && (
                  <div className="empty-state">
                    <h3>
                      {searchTerm
                        ? (t('noResultsFound') || 'Keine Ergebnisse gefunden')
                        : (t('noBookmarks') || 'Keine Lesezeichen vorhanden')
                      }
                    </h3>
                    <p>
                      {searchTerm
                        ? (t('tryDifferentSearch') || 'Versuchen Sie eine andere Suche')
                        : (t('noBookmarksDesc') || 'Fügen Sie Ihr erstes Lesezeichen hinzu')
                      }
                    </p>
                    {/* addFirstBookmark button removed - user can use header + button instead */}
                  </div>
                )}
              </>
            )}

            {/* Voice Control removed from here - now in HamburgerMenu */}
          </div>
        );
        
      case 'reminders':
        return <RemindersTab />;

      default:
        return <div>Tab not found</div>;
    }
  };

  // Don't show anything while auth is loading
  if (authLoading) {
    return (
      <div className="app">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  // Show auth form if not authenticated
  if (!isAuthenticated) {
    return (
      <div className="app">
        <header className="app-header">
          <div className="language-switcher">
            <button
              className={`language-btn ${language === 'en' ? 'active' : ''}`}
              onClick={() => switchLanguage('en')}
              title="English"
            >
              🇺🇸
            </button>
            <button
              className={`language-btn ${language === 'de' ? 'active' : ''}`}
              onClick={() => switchLanguage('de')}
              title="Deutsch"
            >
              🇩🇪
            </button>
          </div>
          
          {/* Voice controls removed - now in HamburgerMenu */}
          
          <h1>myboki.me</h1>
        </header>
        <AuthForm />
      </div>
    );
  }

  // 🧭 ONBOARDING: einmaliger Wizard, bevor die eigentliche App erscheint.
  // Nach Abschluss (oder "Überspringen") öffnet sich die App normal.
  if (showOnboarding) {
    return <OnboardingFlow onFinish={handleOnboardingFinish} />;
  }

  // Main App (Authenticated)
  // Share selected bookmark or app
  const handleShareApp = async () => {
    // If exactly one bookmark is selected, share its original URL
    let shareUrl = window.location.href;
    let shareTitle = 'myboki.me';
    let shareText = t('shareBookmark') || 'Bookmark teilen';

    if (selectedBookmarks.size === 1) {
      const selectedId = Array.from(selectedBookmarks)[0];
      const selectedBookmark = bookmarks.find(b => b.id === selectedId);
      if (selectedBookmark?.url) {
        shareUrl = selectedBookmark.url;
        shareTitle = selectedBookmark.title || 'Bookmark';
        shareText = selectedBookmark.description || `${shareTitle} - ${shareUrl}`;
      }
    }

    const shareData = {
      title: shareTitle,
      text: shareText,
      url: shareUrl
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else if (navigator.clipboard) {
        await navigator.clipboard.writeText(shareUrl);
        alert('Link kopiert!');
      }
    } catch (e) {
      try {
        if (navigator.clipboard) {
          await navigator.clipboard.writeText(shareUrl);
          alert('Link kopiert!');
        }
      } catch (_) {
        console.warn('Share not available');
      }
    }
  };

  return (
    <div className="app">
      {/* Offline Banner */}
      <OfflineBanner />

      {/* Conflict Resolution Modal */}
      <ConflictResolutionModal />

      {/* Mobile Header / App Bar */}
      <MobileHeader
        onAddBookmark={handleAddBookmark}
        onModeChange={handleModeChange}
        forceOnlineMode={forceOnlineMode}
        apiAvailable={apiAvailable}
        onImportClick={handleImportClick}
        premiumUser={premiumUser}
        bookmarks={bookmarks}
        onOpenPanel={handleOpenPanel}
        onOpenRecipePanel={openRecipePanel}
        activeTab={activeTab}
        activeRecipePanel={activeRecipePanel}
      />

      {/* Tab Navigation */}
      <TabNavigation
        activeTab={activeTab}
        activeRecipePanel={activeRecipePanel}
        onTabChange={handleTabChange}
        onOpenRecipePanel={openRecipePanel}
        navItems={navbarConfig}
      />

      <main className="app-main">        
        {/* Error Message */}
        {error && (
          <div className={`error-message ${error.startsWith('✅') ? 'success' : ''}`}>
            {error}
            <button onClick={() => setError(null)} className="error-close">×</button>
          </div>
        )}


        {/* Tab Content */}
        {renderTabContent()}
      </main>

      {/* Import Dialog */}
      <ImportDialog
        isOpen={showImportDialog}
        onClose={() => setShowImportDialog(false)}
        onImport={handleImport}
        loading={importLoading}
      />

      {/* Premium Upgrade Modal */}
      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        feature={upgradeFeature}
      />

      {/* Data Sync Dialog */}
      <DataSyncDialog
        isOpen={showSyncDialog}
        onClose={() => setShowSyncDialog(false)}
        onSyncComplete={handleSyncComplete}
      />

      {/* Share Join Handler */}
      {shareToken && (
        <JoinShareHandler
          token={shareToken}
          onClose={handleShareClose}
          onSuccess={handleShareJoinSuccess}
        />
      )}

      <footer className="app-footer">
        <p>{t('totalBookmarks')} {bookmarks.length}</p>
        <span className="footer-info">myboki.me v{APP_VERSION}</span>
      </footer>
    </div>
  );
};

export default App;